import { useShowFeedbackModalNow } from "@components/feedback-modal";
import {
  StudioRedoableStateType,
  StudioStateType,
} from "@features/studio/use-studio";
import { useGlobalState } from "@features/utils/use-global-state";
import posthog from "posthog-js";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { fetchServer } from "../utils";
import { ImagesType } from "./type";
import { useUpdateImageCache } from "./use-images";

export const useImageActions = (id: string) => {
  const queryClient = useQueryClient();
  const updateCache = useUpdateImageCache();
  const navigate = useNavigate();
  const { site } = useParams();
  const [saving, setSaving] = useGlobalState("images-" + id, false);
  const showFeedbackModalNow = useShowFeedbackModalNow();

  const remove = async (
    image: ImagesType | undefined,
    setPendingUpdate: (v: boolean) => void,
    redirectTo?: string,
  ) => {
    setPendingUpdate(true);
    try {
      if (!image) return;

      let updated: ImagesType;

      if (image.archived) {
        updated = { ...image, deleted: true } as ImagesType;
        updateCache([updated], true);
        await fetchServer(`/api/images/${id}/delete`, {});

        if (redirectTo) navigate(redirectTo || `/${site}/`);
      } else {
        updated = { ...image, archived: true };
        updateCache([updated]);
        await fetchServer(`/api/images/${id}`, { archived: true });
      }
    } catch (e) {
      toast.error("Failed to remove image");
      throw e;
    } finally {
      queryClient.invalidateQueries(["images", "list"]);
      setPendingUpdate(false);
    }
  };

  const restore = async (
    image: ImagesType | undefined,
    setPendingUpdate: (v: boolean) => void,
  ) => {
    setPendingUpdate(true);
    try {
      if (!image) return;

      const updated = { ...image, archived: false };
      updateCache([updated]);
      await fetchServer(`/api/images/${id}`, { archived: false });
    } catch (e) {
      toast.error("Failed to restore image");
      throw e;
    } finally {
      queryClient.invalidateQueries(["images", "list"]);
      setPendingUpdate(false);
    }
  };

  const download = (image: ImagesType | undefined) => {
    posthog.capture("download", {});
    if (navigator.share && image?.url) {
      (async () => {
        const response = await fetch(image?.url);
        const blob = await response.blob();
        const file = new File(
          [blob],
          image?.id + (image?.url?.match(/(\.[^/.]+)$/)?.[0] || ".jpg"),
          { type: blob.type },
        );
        const isMobile =
          window.innerWidth <= 768 || (window as any).ReactNativeWebView;
        if (
          navigator.canShare &&
          navigator.canShare({ files: [file] }) &&
          isMobile
        ) {
          try {
            navigator.share({
              title: "Photographe.ai",
              text: "Generated with Photographe.ai",
              files: [file],
            });
            return;
          } catch (error) {
            console.error("Error sharing image:", error);
          }
        }

        const a = document.createElement("a");
        a.href = image?.url || "";
        a.download = image?.id || "";
        a.click();
      })();
      return;
    }
    const a = document.createElement("a");
    a.href = image?.url || "";
    a.download = image?.id || "";
    a.click();
  };

  const upscale = async (image: ImagesType | undefined, resolution = 2) => {
    if (!image) return;
    try {
      const result: { id: string } = await fetchServer(
        `/api/images/${id}/upscale`,
        {
          upscale_factor: resolution,
        },
      );
      console.log("Upscale result", result);
      navigate(`/${site}/studio/${result.id}`);
      toast.success("Upscale started");
    } catch (e) {
      toast.error("Failed to upscale image");
      throw e;
    } finally {
      queryClient.invalidateQueries(["images", "list"]);
    }
  };

  const magic = async (image: ImagesType | undefined) => {
    if (!image) return;
    try {
      const result: { id: string } = await fetchServer(
        `/api/images/${id}/magic`,
        {},
      );
      console.log("Magic improve result", result);
      navigate(`/${site}/studio/${result.id}`);
      toast.success("Magic improve started");
      showFeedbackModalNow(10000);
    } catch (e) {
      toast.error("Failed to apply magic improve");
      throw e;
    } finally {
      queryClient.invalidateQueries(["images", "list"]);
    }
  };

  const changeProjectId = async (
    image: ImagesType | undefined,
    projectId: string,
  ) => {
    if (!image) return;
    if (image.project_id === projectId) return;
    try {
      const updated = { ...image, project_id: projectId };
      updateCache([updated]);
      await fetchServer(`/api/images/${id}`, { project_id: projectId });
      toast.success("Project updated");
    } catch (e) {
      toast.error("Failed to change project ID");
      throw e;
    } finally {
      queryClient.invalidateQueries(["images", "list"]);
    }
  };

  const restyle = async (
    image: ImagesType | undefined,
    styleId: string,
    fromImage?: File | string,
    prompt?: string,
    count = 1,
  ) => {
    if (!image) return;
    try {
      let fromImageB64: string | undefined;
      if (fromImage && typeof fromImage !== "string") {
        const reader = new FileReader();
        reader.readAsDataURL(fromImage as unknown as File);
        fromImageB64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            if (typeof reader.result === "string") {
              resolve(reader.result);
            } else {
              reject(new Error("FileReader result is not a string"));
            }
          };
          reader.onerror = () => {
            reject(new Error("FileReader encountered an error"));
          };
        });
      } else if (fromImage) {
        fromImageB64 = image as unknown as string;
      }

      const payload: any = { id: styleId, count };
      if (prompt) payload.prompt = prompt;
      if (fromImageB64) payload.image = fromImageB64;

      const result: { id: string }[] = await fetchServer(
        `/api/images/${id}/restyle`,
        payload,
      );
      console.log("Restyle result", result);
      navigate(`/${site}/studio/${result[0].id}`);
      toast.success("Restyle started");
    } catch (e) {
      toast.error("Failed to restyle image");
      throw e;
    } finally {
      queryClient.invalidateQueries(["images", "list"]);
    }
  };

  const extend = async (
    image: ImagesType | undefined,
    withHolesImage: string,
    prompt?: string,
    count = 1,
    attachments?: string[],
    loraId?: string,
    chips?: {
      number: number;
      x: number;
      y: number;
      prompt: string;
      attachment?: string;
      lora_id?: string;
    }[],
    basicExtends = false,
  ) => {
    if (!image) return;
    try {
      const result: { id: string }[] = await fetchServer(
        `/api/images/${id}/extends`,
        {
          image: withHolesImage,
          prompt,
          count,
          attachments,
          lora_id: loraId,
          chips,
          basic_extends: basicExtends,
        },
      );
      console.log("Extend result", result);
      navigate(`/${site}/studio/${result[0].id}`);
      toast.success("Extension started");
    } catch (e) {
      toast.error("Failed to extend image");
      throw e;
    } finally {
      queryClient.invalidateQueries(["images", "list"]);
    }
  };

  // Remove background and optionally replace it
  const background = async (
    image: ImagesType | undefined,
    newBackground?: string, // Background gets replaced if set
    prompt?: string, // Background gets replaced if set
    count = 1,
  ) => {
    if (!image) return;
    try {
      const result: { id: string }[] = await fetchServer(
        `/api/images/${id}/background`,
        {
          prompt,
          background: newBackground,
          count,
        },
      );
      console.log("Background result", result);
      navigate(`/${site}/studio/${result[0].id}`);
      toast.success("Background processing started");
    } catch (e) {
      toast.error("Failed to process background");
      throw e;
    } finally {
      queryClient.invalidateQueries(["images", "list"]);
    }
  };

  const createVideo = async (
    image: ImagesType | undefined,
    options: {
      prompt?: string;
      audio?: string | boolean; // Base64 encoded audio
      quality: "low" | "medium" | "high";
      duration: number; // in seconds
    },
  ) => {
    if (!image) return;
    try {
      const result: { id: string } = await fetchServer(
        `/api/images/${id}/video`,
        {
          prompt: options.prompt,
          audio: options.audio,
          quality: options.quality,
          duration: options.duration,
        },
      );
      console.log("Video creation result", result);
      navigate(`/${site}/studio/${result.id}`);
      toast.success("Video creation started");
    } catch (e) {
      toast.error("Failed to create video");
      throw e;
    } finally {
      queryClient.invalidateQueries(["images", "list"]);
    }
  };

  const edit = async (
    image: ImagesType | undefined,
    edits: Partial<StudioStateType>,
    undos: {
      undos: StudioRedoableStateType[];
      redos: StudioRedoableStateType[];
    },
    imageData: string,
  ) => {
    if (!image || !imageData) return;
    try {
      setSaving(true);
      const result: { id: string } = await fetchServer(
        `/api/images/${id}/edits`,
        {
          image: imageData,
          undos: undos,
          frame: edits.frame,
          effects: edits.effects,
          transform: edits.transform,
          drawing: edits.drawing,
        },
      );
      console.log("Save and render edits", result);
    } catch (e) {
      throw e;
    } finally {
      setSaving(false);
      queryClient.invalidateQueries(["images", "list"]);
      queryClient.refetchQueries(["images", image.id]);
    }
  };

  const toggleFavorite = async (
    image: ImagesType | undefined,
    setPendingUpdate: (v: boolean) => void,
  ) => {
    posthog.capture("toggle_favorite", {});
    setPendingUpdate(true);
    try {
      if (!image) return;
      setSaving(true);

      const updated = { ...image, favorite: !image.favorite };
      updateCache([updated]);
      await fetchServer(`/api/images/${id}`, { favorite: !image.favorite });
      toast.success(
        updated.favorite ? "Added to favorites" : "Removed from favorites",
      );

      showFeedbackModalNow(10000);
    } catch (e) {
      toast.error("Failed to update favorite status");
      throw e;
    } finally {
      queryClient.invalidateQueries(["images", "list"]);
      setPendingUpdate(false);
      setSaving(false);
    }
  };

  const tryOn = async (
    image: ImagesType | undefined,
    fromImage: File | string,
    count = 1,
  ) => {
    if (!image) return;
    try {
      let fromImageB64: string | undefined;
      if (fromImage && typeof fromImage !== "string") {
        const reader = new FileReader();
        reader.readAsDataURL(fromImage as unknown as File);
        fromImageB64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            if (typeof reader.result === "string") {
              resolve(reader.result);
            } else {
              reject(new Error("FileReader result is not a string"));
            }
          };
          reader.onerror = () => {
            reject(new Error("FileReader encountered an error"));
          };
        });
      } else {
        fromImageB64 = fromImage as string;
      }

      const result: { id: string }[] = await fetchServer(
        `/api/images/${id}/tryon`,
        {
          from_image: fromImageB64,
          count,
        },
      );
      console.log("Try-on result", result);
      navigate(`/${site}/studio/${result[0].id}`);
      toast.success("Try-on started");
    } catch (e) {
      toast.error("Failed to apply try-on");
      throw e;
    } finally {
      queryClient.invalidateQueries(["images", "list"]);
    }
  };

  const haircut = async (
    image: ImagesType | undefined,
    fromImage: File | string,
    count = 1,
  ) => {
    if (!image) return;
    try {
      let fromImageB64: string | undefined;
      if (fromImage && typeof fromImage !== "string") {
        const reader = new FileReader();
        reader.readAsDataURL(fromImage as unknown as File);
        fromImageB64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            if (typeof reader.result === "string") {
              resolve(reader.result);
            } else {
              reject(new Error("FileReader result is not a string"));
            }
          };
          reader.onerror = () => {
            reject(new Error("FileReader encountered an error"));
          };
        });
      } else {
        fromImageB64 = fromImage as string;
      }

      const result: { id: string }[] = await fetchServer(
        `/api/images/${id}/haircut`,
        {
          from_image: fromImageB64,
          count,
        },
      );
      console.log("Haircut result", result);
      navigate(`/${site}/studio/${result[0].id}`);
      toast.success("Haircut started");
    } catch (e) {
      toast.error("Failed to apply haircut");
      throw e;
    } finally {
      queryClient.invalidateQueries(["images", "list"]);
    }
  };

  const faceEffect = async (
    image: ImagesType | undefined,
    effect: string,
    makeupDescription?: string,
    count = 1,
  ) => {
    if (!image) return;
    try {
      const result: { id: string }[] = await fetchServer(
        `/api/images/${id}/uprealism`,
        {
          style: effect,
          makeup_description: makeupDescription,
          count,
        },
      );
      console.log("Face effect result", result);
      navigate(`/${site}/studio/${result[0].id}`);
      toast.success("Face effect started");
    } catch (e) {
      toast.error("Failed to apply face effect");
      throw e;
    } finally {
      queryClient.invalidateQueries(["images", "list"]);
    }
  };

  return {
    remove,
    restore,
    download,
    upscale,
    magic,
    changeProjectId,
    restyle,
    extend,
    background,
    createVideo,
    edit,
    toggleFavorite,
    tryOn,
    haircut,
    faceEffect,
    saving,
  };
};
